import { NEUTRAL_COLOR, REGION_MIN_POINTS, regionColor, type AtlasMap, type MapGrid, type MapMarker, type MapRegion, type MarkerCategory, type MarkerStyle } from './atlas.ts';
import type { MapDrawing, MapLayer } from './drawings.ts';
import { isRevealed, parseFog, revealsAny } from './fog.ts';
import { centroid, type MapPoint } from './geometry.ts';
import { effectiveIcon, resolveIcon, type MapIcon, type ResolvedIcon } from './icons.ts';

/**
 * What the Player View is sent (docs/PARITY-STEP-4-PLAN.md section 9, decision 4): one map as
 * players may see it, worked out in the GM's window before anything reaches the Player View,
 * as the desktop's `AtlasPlayerView` draws it:
 * - pins and regions marked GM only never appear; nor do pins under the fog, or regions
 *   with no part revealed;
 * - only the drawings on layers players can see, and of those only the ones with some part
 *   revealed, as the desktop's player-facing exports leave them out (`MapExport.shown`),
 *   so nothing in unexplored parts is sent at all;
 * - a region's name only where its middle is revealed, and not where a pin players can see
 *   already carries it;
 * - no notes, no links, no ids of entries or other maps, no categories' names.
 * The fog itself goes as it's stored, for the Player View to cover the map with, solid.
 */

export interface PlayerPin {
  readonly x: number;
  readonly y: number;
  readonly label: string;
  readonly style: MarkerStyle;
  readonly color: string;
  readonly icon: ResolvedIcon | null;
}

export interface PlayerRegion {
  readonly points: readonly MapPoint[];
  readonly color: string;
}

export interface PlayerFrame {
  readonly mapId: string;
  readonly name: string;
  /** Changes when the picture does, so the Player View knows to ask for it again. */
  readonly imageKey: string;
  readonly grid: MapGrid;
  readonly regions: readonly PlayerRegion[];
  /** Region names, where they go. */
  readonly regionLabels: readonly { readonly at: MapPoint; readonly label: string }[];
  readonly drawings: readonly MapDrawing[];
  readonly pins: readonly PlayerPin[];
  readonly fog: { readonly enabled: boolean; readonly revealed: string };
}

export function playerFrame(input: {
  readonly map: Pick<AtlasMap, 'id' | 'name' | 'mimeType' | 'imageData' | 'grid' | 'fog'>;
  readonly markers: readonly MapMarker[];
  readonly regions: readonly MapRegion[];
  readonly categories: readonly MarkerCategory[];
  readonly icons: readonly MapIcon[];
  readonly layers: readonly MapLayer[];
  readonly drawings: readonly MapDrawing[];
}): PlayerFrame {
  const { map } = input;
  const enabled = map.fog.enabled;
  const revealed = parseFog(map.fog.revealed);
  const categories = new Map(input.categories.map((c) => [c.id, c]));
  const icons = new Map(input.icons.map((i) => [i.id, i]));
  const seen = input.markers.filter((m) => m.visible && isRevealed(enabled, revealed, m));
  const regions = input.regions.filter((r) => r.visible && r.points.length >= REGION_MIN_POINTS && revealsAny(enabled, revealed, r.points));
  const pinNames = new Set(seen.map((m) => m.label.trim().toLowerCase()));
  const shownLayers = new Set(input.layers.filter((l) => l.playersCanSee).map((l) => l.id));
  return {
    mapId: map.id,
    name: map.name,
    imageKey: `${map.id}:${map.mimeType}:${map.imageData.byteLength}`,
    grid: map.grid,
    regions: regions.map((r) => ({ points: r.points, color: regionColor(r, r.categoryId ? categories.get(r.categoryId) : undefined) })),
    regionLabels: regions.flatMap((r) => {
      const at = centroid(r.points);
      const label = r.label.trim();
      return label !== '' && isRevealed(enabled, revealed, at) && !pinNames.has(label.toLowerCase()) ? [{ at, label: r.label }] : [];
    }),
    drawings: input.drawings.filter((d) => shownLayers.has(d.layerId) && revealsAny(enabled, revealed, d.points)),
    pins: seen.map((m) => {
      const category = m.categoryId ? categories.get(m.categoryId) : undefined;
      return { x: m.x, y: m.y, label: m.label, style: m.style, color: category?.colorHex ?? NEUTRAL_COLOR, icon: resolveIcon(effectiveIcon(m.icon, category?.icon ?? ''), (id) => icons.get(id)) };
    }),
    fog: { enabled, revealed: map.fog.revealed },
  };
}
